import React, { useState, useContext, useEffect } from 'react';
import { Box, Heading, Grid, ResponsiveContext, Spinner, Text } from 'grommet';
import { DashboardCard } from '.';
import { loadIcon } from '../utils/IconUtils';
import { getCSVData, getJSONData, getFileTypeFromPath } from '../utils/DataUtils';

export const DashboardGrid = ({ filePath, ...rest }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const size = useContext(ResponsiveContext);

  const processRow = async (row) => {
    const { section, title, description, cta, url, icon, iconColor } = row;
    if (!section || !title || !description) return null;

    let IconComponent = await loadIcon(icon) || await loadIcon('Link');
    const iconColorValue = iconColor || 'green';

    return {
      section,
      item: { title, description, cta, url, icon: <IconComponent color={iconColorValue} /> }
    };
  };

  const groupData = (rawData) => {
    const grouped = rawData.reduce((acc, item) => {
      if (item) {
        if (!acc[item.section]) {
          acc[item.section] = { section: item.section, items: [] };
        }
        acc[item.section].items.push(item.item);
      }
      return acc;
    }, {});
    return Object.values(grouped);
  };

  useEffect(() => {
    const processData = async () => {
      try {
        const fileType = getFileTypeFromPath(filePath);
        let rawData = [];

        if (fileType === 'csv') {
          rawData = await getCSVData(filePath);
        } else if (fileType === 'json') {
          rawData = await getJSONData(filePath);
        }

        const processedData = await Promise.all(rawData.map(processRow));
        const groupedData = groupData(processedData);

        setData(groupedData);
      } catch (error) {
        console.error('Error processing data:', error);
        setError("An error occurred while loading the data.");
      } finally {
        setLoading(false);
      }
    };

    processData();
  }, [filePath]);

  const getGridColumns = () => (['xsmall', 'small'].includes(size) ? '100%' : 'medium');

  if (loading) {
    return (
      <Box align="center" justify="center" fill>
        <Spinner size="medium" color="brand" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box align="center" justify="center" fill>
        <Text color="status-critical" weight="bold">{error}</Text>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box align="center" justify="center" fill>
        <Text weight="bold">No data available. Please try again later.</Text>
      </Box>
    );
  }

  return (
    <Box pad={{ bottom: 'medium' }} direction="column" {...rest}>
      {data.map((section, sectionIndex) => (
        <Box key={sectionIndex} pad={{ bottom: 'small' }} direction="column" gap="small">
          {section.section && (
            <Heading size="small" weight="bold" margin={{ top: 'large', bottom: 'xsmall' }}>
              {section.section}
            </Heading>
          )}
          <Grid columns={getGridColumns()} gap="medium" fill>
            {section.items && section.items.map((item, itemIndex) => (
              <Box key={itemIndex} direction="row" align="center">
                <DashboardCard key={itemIndex} card={item} />
              </Box>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );
};
